/**
 * FBA Shipments screen (M3) — /app/shipments, visible only when the
 * 'shipments' module is enabled for the tenant. Every shipment here was
 * created by the user in Seller Central; Sourcito only records it and
 * lot-costs the units (hard rule: no automated Amazon writes). Same visual
 * conventions as OrdersPage: nav pattern, Card/Badge, expandable rows.
 */
import { useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronUp, ChevronsUpDown, Search } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '../../design';
import {
  useImportShipments,
  useMarkShipped,
  canRecordCheckIn,
  DEFAULT_SHIPMENT_FILTER,
  filterFbaShipments,
  isAgingShipment,
  lineShortfallTotal,
  netUnrecoveredForDisplay,
  receivedLineStatus,
  searchFbaShipments,
  shipmentCaseStatusBadge,
  shipmentFilterCounts,
  SHIPMENT_FILTER_PILLS,
  shipmentLinePreviewLabel,
  shipmentPreviewMoreLabel,
  shipmentQtyStory,
  shortDateLabel,
  shortageBadgeLabel,
  sortFbaShipments,
  unallocatedBadgeLabel,
  useCreateShipment,
  useCreateShipmentCase,
  useDeleteShipmentCase,
  useFbaShipment,
  useFbaShipments,
  useRecordReceived,
  useShipmentSearchLines,
  useUpdateShipmentCase,
  useUploadShipmentFile,
  type ReceivedStatusVariant,
  type ShipmentFilterKey,
  type ShipmentSortDirection,
  type ShipmentSortKey,
} from '../../lib/shipments';
import { useModuleEnabled } from '../../lib/auth';
import { ApiCallError } from '../../lib/api';
import { AppShell } from '../../components/AppShell';
import { ProductThumb } from '../../components/ProductThumb';
import { ProductLink } from '../../components/ProductLink';
import { Skeleton, SkeletonLine } from '../../components/Skeleton';
import type {
  FbaShipmentDetail,
  FbaShipmentLineDetail,
  FbaShipmentStatus,
  FbaShipmentSummary,
  ShipmentCase,
} from '@sourcito/schemas';

const STATUS_BADGE: Record<
  FbaShipmentStatus,
  { label: string; variant: 'neutral' | 'info' | 'warning' | 'positive' }
> = {
  preparing: { label: 'Preparing', variant: 'neutral' },
  shipped: { label: 'Shipped', variant: 'info' },
  receiving: { label: 'Receiving', variant: 'warning' },
  closed: { label: 'Closed', variant: 'positive' },
};

const money = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** Plain-sentence empty states for a filter pill that zeroed out the list
 *  (as opposed to a search query — that gets its own message). 'all' is
 *  unreachable in practice (an empty shipments array is caught earlier), but
 *  every ShipmentFilterKey needs an entry for the Record to type-check. */
const SHIPMENT_FILTER_EMPTY: Record<ShipmentFilterKey, string> = {
  all: 'No shipments found.',
  needs_attention: 'No shipments need attention.',
  open: 'No open shipments.',
  closed: 'No closed shipments.',
};

const RECEIVED_STATUS_CLASS: Record<ReceivedStatusVariant, string> = {
  quiet: 'text-fg-tertiary',
  positive: 'text-positive',
  warning: 'text-warning',
  negative: 'text-negative',
};

export function ShipmentsPage() {
  const enabled = useModuleEnabled('shipments');
  const { data: shipments, isLoading, isError } = useFbaShipments(enabled);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ShipmentFilterKey>(DEFAULT_SHIPMENT_FILTER);
  // null = default order as returned by the API — no column sort applied yet.
  const [sort, setSort] = useState<{
    key: ShipmentSortKey;
    direction: ShipmentSortDirection;
  } | null>(null);

  const shipmentIds = useMemo(() => (shipments ?? []).map((s) => s.id), [shipments]);
  const searchLines = useShipmentSearchLines(shipmentIds, query.trim().length > 0);
  const filterCounts = useMemo(() => shipmentFilterCounts(shipments ?? []), [shipments]);

  const visibleShipments = useMemo(() => {
    const searched = shipments ? searchFbaShipments(shipments, searchLines, query) : [];
    const filtered = filterFbaShipments(searched, filter);
    return sort ? sortFbaShipments(filtered, sort.key, sort.direction) : filtered;
  }, [shipments, searchLines, query, filter, sort]);

  function toggleSort(key: ShipmentSortKey) {
    setSort((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: key === 'shipmentId' ? 'asc' : 'desc' };
    });
  }

  if (!enabled) {
    return (
      <AppShell>
        <main className="max-w-3xl mx-auto px-8 py-16">
          <Card>
            <p className="text-sm text-fg-secondary">
              The Shipments module isn't enabled for this workspace.{' '}
              <Link to="/app/brands" className="text-accent-fg hover:underline">
                Back to Brands
              </Link>
            </p>
          </Card>
        </main>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <header className="border-b border-border-subtle">
        <div className="max-w-3xl mx-auto px-8 py-4">
          <h1 className="text-xl font-medium leading-tight">FBA Shipments</h1>
          <p className="text-xs text-fg-tertiary">
            {shipments ? `${shipments.length} shipment${shipments.length === 1 ? '' : 's'}` : ' '}
          </p>
          <Link to="/app/warehouse/packing" className="text-sm text-accent-fg hover:underline">
            Open packing workspace
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-8 py-8 space-y-3">
        <ImportFromAmazonCard />
        <Card>
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="w-full flex items-center justify-between gap-3 text-left"
          >
            <p className="text-sm font-medium text-fg-primary">Record shipment manually</p>
            <span className="text-xs text-fg-tertiary">{showCreate ? 'Hide' : 'Show'}</span>
          </button>
          {showCreate && <CreateShipmentForm />}
        </Card>

        {isLoading && <ShipmentCardSkeletons />}
        {isError && <p className="text-sm text-negative">Could not load shipments.</p>}
        {shipments?.length === 0 && (
          <Card>
            <p className="text-sm text-fg-secondary">
              No FBA shipments recorded yet. Record one above once it exists in Seller Central.
            </p>
          </Card>
        )}
        {shipments && shipments.length > 0 && (
          <>
            <div
              className="flex items-center gap-2 flex-wrap"
              role="tablist"
              aria-label="Shipment filter"
            >
              {SHIPMENT_FILTER_PILLS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === p.key}
                  onClick={() => setFilter(p.key)}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors duration-fast',
                    filter === p.key
                      ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
                      : 'border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
                  )}
                >
                  {p.label} · {filterCounts[p.key]}
                </button>
              ))}
            </div>
            <div className="relative max-w-sm">
              <Search
                className="h-4 w-4 text-fg-tertiary absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                aria-hidden="true"
              />
              <Input
                inputSize="sm"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search shipment id, name, or SKU..."
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-fg-tertiary mr-0.5">Sort:</span>
              <SortButton
                label="Shipped"
                active={sort?.key === 'shippedAt'}
                direction={sort?.key === 'shippedAt' ? sort.direction : 'desc'}
                onClick={() => toggleSort('shippedAt')}
              />
              <SortButton
                label="Status"
                active={sort?.key === 'status'}
                direction={sort?.key === 'status' ? sort.direction : 'desc'}
                onClick={() => toggleSort('status')}
              />
              <SortButton
                label="Shipment ID"
                active={sort?.key === 'shipmentId'}
                direction={sort?.key === 'shipmentId' ? sort.direction : 'asc'}
                onClick={() => toggleSort('shipmentId')}
              />
              <SortButton
                label="Units"
                active={sort?.key === 'units'}
                direction={sort?.key === 'units' ? sort.direction : 'desc'}
                onClick={() => toggleSort('units')}
              />
            </div>
            {visibleShipments.length === 0 && (
              <Card>
                <p className="text-sm text-fg-secondary">
                  {query.trim() ? `No shipments match "${query}".` : SHIPMENT_FILTER_EMPTY[filter]}
                </p>
              </Card>
            )}
          </>
        )}
        {visibleShipments.map((s) => {
          const badge = STATUS_BADGE[s.status];
          const open = openId === s.id;
          const moreLabel = shipmentPreviewMoreLabel(s.lineCount, s.linePreview.length);
          return (
            <Card key={s.id}>
              <button
                type="button"
                onClick={() => setOpenId(open ? null : s.id)}
                className="w-full flex items-center justify-between gap-3 text-left"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-fg-primary">
                    <span className="font-mono">{s.shipmentId}</span>
                  </p>
                  <p className="text-xs text-fg-tertiary">
                    {shipmentQtyStory(s)}
                    {s.destinationFc && ` · to ${s.destinationFc}`}
                  </p>
                  {s.linePreview.map((l) => (
                    <p key={l.sellerSku} className="text-xs text-fg-tertiary truncate">
                      <span className="font-mono">{l.sellerSku}</span>{' '}
                      {shipmentLinePreviewLabel(l.productName, l.qty)}
                    </p>
                  ))}
                  {moreLabel && <p className="text-xs text-fg-tertiary truncate">{moreLabel}</p>}
                  <ShipmentAttentionBadges shipment={s} />
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-xs text-fg-tertiary">
                    {shortDateLabel(s.shippedAt ?? s.createdAt)}
                  </span>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </div>
              </button>
              {open && <ShipmentDetailSection id={s.id} />}
            </Card>
          );
        })}
      </main>
    </AppShell>
  );
}

/** Skeleton stand-in for the shipment card list while `useFbaShipments`
 *  loads — shape mirrors the real `<Card>` row (shipment id + qty story +
 *  a line-preview row on the left, ship date + status badge on the right). */
function ShipmentCardSkeletons() {
  return (
    <>
      {Array.from({ length: 3 }, (_, i) => (
        <Card key={i}>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-1.5">
              <SkeletonLine className="w-32" />
              <SkeletonLine className="w-44 h-3" />
              <SkeletonLine className="w-36 h-3" />
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <SkeletonLine className="w-14 h-3" />
              <Skeleton className="h-5 w-20 rounded-xs" />
            </div>
          </div>
        </Card>
      ))}
    </>
  );
}

function ShipmentAttentionBadges({ shipment }: { shipment: FbaShipmentSummary }) {
  const short = shortageBadgeLabel(shipment);
  const unallocated = unallocatedBadgeLabel(shipment.unallocatedUnits);
  const aging = isAgingShipment(shipment.status, shipment.createdAt);
  if (!short && !unallocated && !aging) return null;
  return (
    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
      {short && <Badge variant="negative">{short}</Badge>}
      {unallocated && <Badge variant="warning">{unallocated}</Badge>}
      {aging && <Badge variant="negative">aging</Badge>}
    </div>
  );
}

/** Toolbar sort control (card-list equivalent of AmazonPage's SortableTh —
 *  this page uses a Card list, not a table, so sorting is a button row
 *  rather than clickable column headers). */
function SortButton({
  label,
  active,
  direction,
  onClick,
}: {
  label: string;
  active: boolean;
  direction: 'asc' | 'desc';
  onClick: () => void;
}) {
  const Icon = active ? (direction === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-sm border transition-colors duration-fast',
        active
          ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
          : 'border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
      )}
    >
      {label}
      <Icon className="h-3 w-3" aria-hidden="true" />
    </button>
  );
}

interface LineRow {
  sellerSku: string;
  qty: string;
}

function emptyRow(): LineRow {
  return { sellerSku: '', qty: '' };
}

function CreateShipmentForm() {
  const [shipmentId, setShipmentId] = useState('');
  const [name, setName] = useState('');
  const [destinationFc, setDestinationFc] = useState('');
  const [lines, setLines] = useState<LineRow[]>([emptyRow()]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; warn: boolean } | null>(null);
  const create = useCreateShipment();

  function updateLine(i: number, field: keyof LineRow, value: string) {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  }

  function addLine() {
    setLines((prev) => [...prev, emptyRow()]);
  }

  function removeLine(i: number) {
    setLines((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));
  }

  function reset() {
    setShipmentId('');
    setName('');
    setDestinationFc('');
    setLines([emptyRow()]);
  }

  const validLines = lines
    .map((l) => ({ sellerSku: l.sellerSku.trim(), qty: Number(l.qty) }))
    .filter((l) => l.sellerSku && Number.isInteger(l.qty) && l.qty > 0);

  const canSubmit = shipmentId.trim().length >= 4 && validLines.length > 0 && !create.isPending;

  async function handleSubmit() {
    setError(null);
    setNotice(null);
    if (!canSubmit) return;
    try {
      const result = await create.mutateAsync({
        shipmentId: shipmentId.trim(),
        name: name.trim() || undefined,
        destinationFc: destinationFc.trim() || undefined,
        lines: validLines,
      });
      setNotice(
        result.totalUnallocated > 0
          ? {
              text: `recorded — ${result.totalUnallocated} unit${result.totalUnallocated === 1 ? '' : 's'} could not be allocated from stock`,
              warn: true,
            }
          : { text: 'recorded', warn: false }
      );
      reset();
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Could not record shipment.');
    }
  }

  return (
    <div className="mt-3 border-t border-border-subtle pt-3 space-y-4">
      <div className="flex gap-3 flex-wrap">
        <FormField label="Shipment ID" htmlFor="shipment-id" required className="w-40">
          <Input
            id="shipment-id"
            value={shipmentId}
            onChange={(e) => setShipmentId(e.target.value)}
            placeholder="FBA15ABCDE"
          />
        </FormField>
        <FormField label="Name" htmlFor="shipment-name" className="flex-1 min-w-40">
          <Input id="shipment-name" value={name} onChange={(e) => setName(e.target.value)} />
        </FormField>
        <FormField label="Destination FC" htmlFor="shipment-fc" className="w-32">
          <Input
            id="shipment-fc"
            value={destinationFc}
            onChange={(e) => setDestinationFc(e.target.value)}
            placeholder="ONT8"
          />
        </FormField>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-fg-primary">Lines</p>
        {lines.map((l, i) => (
          <div key={i} className="flex items-end gap-3">
            <FormField label="Seller SKU" htmlFor={`sku-${i}`} className="flex-1">
              <Input
                id={`sku-${i}`}
                value={l.sellerSku}
                onChange={(e) => updateLine(i, 'sellerSku', e.target.value)}
              />
            </FormField>
            <FormField label="Qty" htmlFor={`qty-${i}`} className="w-24">
              <Input
                id={`qty-${i}`}
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={l.qty}
                onChange={(e) => updateLine(i, 'qty', e.target.value)}
              />
            </FormField>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={lines.length === 1}
              onClick={() => removeLine(i)}
            >
              Remove
            </Button>
          </div>
        ))}
        <Button type="button" variant="secondary" size="sm" onClick={addLine}>
          Add line
        </Button>
      </div>

      {error && <p className="text-sm text-negative">{error}</p>}
      {notice && (
        <p className={`text-sm ${notice.warn ? 'text-warning' : 'text-positive'}`}>{notice.text}</p>
      )}

      <Button type="button" onClick={handleSubmit} disabled={!canSubmit} loading={create.isPending}>
        Record shipment
      </Button>
    </div>
  );
}

function ShipmentDetailSection({ id }: { id: string }) {
  const { data, isLoading } = useFbaShipment(id);
  if (isLoading) return <ShipmentDetailSkeleton />;
  if (!data) return null;
  const shortfall = lineShortfallTotal(data.lines);
  return (
    <div className="mt-3 border-t border-border-subtle pt-3 space-y-4">
      {shortfall > 0 && (
        <Badge variant="negative">
          {shortfall} unit{shortfall === 1 ? '' : 's'} short
        </Badge>
      )}
      {data.notes && <p className="text-sm text-fg-secondary">{data.notes}</p>}
      {data.status === 'preparing' && <MarkShippedButton id={id} />}
      <ShipmentCasesSection shipmentId={id} cases={data.cases} />
      <ShipmentRecoverySection recovery={data.recovery} />
      <ShipmentLinesSection shipmentId={id} status={data.status} lines={data.lines} />
      <ShipmentFilesSection shipmentId={id} files={data.files} />
    </div>
  );
}

/** Skeleton stand-in for `ShipmentDetailSection` while `useFbaShipment`
 *  loads — three section placeholders (header + body block) roughly
 *  matching Cases / Recovery / Lines, the sections that always render. */
function ShipmentDetailSkeleton() {
  return (
    <div className="mt-3 border-t border-border-subtle pt-3 space-y-4">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="space-y-2">
          <SkeletonLine className="w-24" />
          <Skeleton className="h-12 rounded-md" />
        </div>
      ))}
    </div>
  );
}

function ShipmentCasesSection({
  shipmentId,
  cases,
}: {
  shipmentId: string;
  cases: ShipmentCase[];
}) {
  const [showAdd, setShowAdd] = useState(false);
  const update = useUpdateShipmentCase(shipmentId);
  const del = useDeleteShipmentCase(shipmentId);

  function handleDelete(c: ShipmentCase) {
    if (confirm(`Delete case ${c.caseId}? This cannot be undone.`)) {
      del.mutate(c.id);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-fg-primary">
          Cases{cases.length > 0 ? ` (${cases.length})` : ''}
        </p>
        <Button type="button" size="sm" variant="secondary" onClick={() => setShowAdd(true)}>
          Add case
        </Button>
      </div>
      {cases.length === 0 && (
        <p className="text-xs text-fg-tertiary">No Seller Central cases attached yet.</p>
      )}
      {cases.length > 0 && (
        <ul className="divide-y divide-border-subtle -mx-4">
          {cases.map((c) => {
            const badge = shipmentCaseStatusBadge(c.status);
            return (
              <li key={c.id} className="px-4 py-2.5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-fg-primary">
                    <span className="font-mono">{c.caseId}</span>
                    <Badge variant={badge.variant} className="ml-2">
                      {badge.label}
                    </Badge>
                  </p>
                  <p className="text-xs text-fg-tertiary">
                    {c.filedAt
                      ? `filed ${new Date(c.filedAt).toLocaleDateString()}`
                      : 'no filed date'}
                  </p>
                  {c.notes && <p className="text-xs text-fg-secondary mt-1">{c.notes}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {c.status === 'open' && (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={update.isPending}
                        onClick={() => update.mutate({ caseId: c.id, status: 'resolved' })}
                      >
                        Mark resolved
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={update.isPending}
                        onClick={() => update.mutate({ caseId: c.id, status: 'denied' })}
                      >
                        Mark denied
                      </Button>
                    </>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={del.isPending}
                    onClick={() => handleDelete(c)}
                  >
                    Delete
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <AddCaseDialog shipmentId={shipmentId} open={showAdd} onClose={() => setShowAdd(false)} />
    </div>
  );
}

function AddCaseDialog({
  shipmentId,
  open,
  onClose,
}: {
  shipmentId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [caseId, setCaseId] = useState('');
  const [filedAt, setFiledAt] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateShipmentCase(shipmentId);

  function reset() {
    setCaseId('');
    setFiledAt('');
    setNotes('');
    setError(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit() {
    setError(null);
    const trimmedId = caseId.trim();
    if (!trimmedId) return;
    try {
      await create.mutateAsync({
        caseId: trimmedId,
        filedAt: filedAt ? new Date(filedAt).toISOString() : undefined,
        notes: notes.trim() || undefined,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Could not add case.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Seller Central case</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <FormField label="Case ID" htmlFor="case-id" required>
            <Input
              id="case-id"
              value={caseId}
              onChange={(e) => setCaseId(e.target.value)}
              placeholder="1234567890"
              autoFocus
            />
          </FormField>
          <FormField label="Filed date" htmlFor="case-filed-at">
            <Input
              id="case-filed-at"
              type="date"
              value={filedAt}
              onChange={(e) => setFiledAt(e.target.value)}
            />
          </FormField>
          <FormField label="Notes" htmlFor="case-notes">
            <textarea
              id="case-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full text-sm rounded-sm border border-border-subtle bg-canvas px-3 py-2 focus:outline-none focus:ring-1 focus:ring-accent-fg"
            />
          </FormField>
        </div>

        {error && <p className="mt-2 text-sm text-negative">{error}</p>}

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            loading={create.isPending}
            disabled={!caseId.trim()}
            onClick={handleSubmit}
          >
            Add case
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShipmentRecoverySection({ recovery }: { recovery: FbaShipmentDetail['recovery'] }) {
  if (!recovery) return null;
  const {
    matchedReimbursements,
    reimbursedCents,
    shortfallCents,
    shortfallIncomplete,
    netUnrecoveredCents,
  } = recovery;
  if (shortfallCents === 0 && matchedReimbursements.length === 0) return null;
  const netDisplay = netUnrecoveredForDisplay(netUnrecoveredCents);
  return (
    <div className="space-y-2 border-t border-border-subtle pt-3">
      <p className="text-sm font-medium text-fg-primary">Recovery</p>
      <p className="text-sm text-fg-secondary">
        Shortfall at cost: {shortfallIncomplete && '~'}
        {money(shortfallCents)}
      </p>
      {shortfallIncomplete && (
        <p className="text-xs text-fg-tertiary">
          estimate incomplete — some units missing lot cost
        </p>
      )}
      {reimbursedCents > 0 && (
        <p className="text-sm text-fg-secondary">Reimbursed: {money(reimbursedCents)}</p>
      )}
      {matchedReimbursements.length > 0 && (
        <ul className="space-y-1">
          {matchedReimbursements.map((r) => (
            <li key={r.reimbursementId} className="text-xs text-fg-tertiary">
              {new Date(r.approvalDate).toLocaleDateString()} · {r.reason} ·{' '}
              <span className="font-mono">{r.sellerSku}</span>
              {r.caseId && <span> · case {r.caseId}</span>} · {money(r.amountCents)}
            </li>
          ))}
        </ul>
      )}
      <p className="text-sm font-medium text-fg-primary">Net unrecovered: {money(netDisplay)}</p>
    </div>
  );
}

function MarkShippedButton({ id }: { id: string }) {
  const markShipped = useMarkShipped(id);
  return (
    <Button
      size="sm"
      variant="secondary"
      disabled={markShipped.isPending}
      onClick={() => markShipped.mutate()}
    >
      Mark handed to carrier (starts the aging clock)
    </Button>
  );
}

function ShipmentLinesSection({
  shipmentId,
  status,
  lines,
}: {
  shipmentId: string;
  status: FbaShipmentStatus;
  lines: FbaShipmentLineDetail[];
}) {
  // Closed shipments render read-only — no empty "received qty" box that
  // reads as missing data on a fully-checked-in shipment (Faraz 2026-08-25).
  const canRecord = canRecordCheckIn(status);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const recordReceived = useRecordReceived(shipmentId);

  const fieldFor = (sku: string) => inputs[sku] ?? '';

  function setField(sku: string, value: string) {
    if (value !== '' && Number(value) < 0) return;
    setInputs((prev) => ({ ...prev, [sku]: value }));
    setNotice(null);
  }

  const hasAnyInput = lines.some((l) => fieldFor(l.sellerSku) !== '');

  async function handleSubmit() {
    setError(null);
    setNotice(null);
    const payload = lines
      .filter((l) => fieldFor(l.sellerSku) !== '')
      .map((l) => ({ sellerSku: l.sellerSku, fbaReceivedQty: Number(fieldFor(l.sellerSku)) }));
    if (payload.length === 0) return;
    try {
      await recordReceived.mutateAsync({ lines: payload });
      setNotice('FBA check-in recorded.');
      setInputs({});
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Could not record FBA check-in.');
    }
  }

  return (
    <div className="space-y-3">
      <ul className="divide-y divide-border-subtle">
        {lines.map((l) => {
          const status = receivedLineStatus(l.qty, l.fbaReceivedQty);
          return (
            <li key={l.id} className="py-3 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <ProductThumb imageUrl={l.imageUrl} alt={l.productName ?? l.sellerSku} />
                  <div className="min-w-0">
                    <p className="text-sm text-fg-primary truncate">
                      <ProductLink asin={l.asin}>{l.productName ?? l.sellerSku}</ProductLink>
                    </p>
                    {l.productName && (
                      <p className="text-xs font-mono text-fg-tertiary">{l.sellerSku}</p>
                    )}
                    <p className="text-xs text-fg-tertiary">
                      shipped {l.qty}{' '}
                      <span className={RECEIVED_STATUS_CLASS[status.variant]} title={status.title}>
                        {status.text}
                      </span>
                    </p>
                    {l.allocations.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {l.allocations.map((a, i) => (
                          <li key={i} className="text-xs text-fg-tertiary">
                            {a.qtyUnits} units @ {money(a.unitCostCents)}
                            {a.poExternalId
                              ? ` from KeHE #${a.poExternalId}`
                              : ' (unmatched vendor)'}
                          </li>
                        ))}
                      </ul>
                    )}
                    {l.unallocatedUnits > 0 && (
                      <p className="text-xs text-warning">{l.unallocatedUnits} unallocated</p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-fg-tertiary text-right shrink-0">
                  {money(l.landedCostCents)} landed
                </p>
              </div>
              {canRecord && (
                <FormField label="New received qty" htmlFor={`recv-${l.id}`} className="w-40">
                  <Input
                    id={`recv-${l.id}`}
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    placeholder={l.fbaReceivedQty != null ? `currently ${l.fbaReceivedQty}` : ''}
                    value={fieldFor(l.sellerSku)}
                    onChange={(e) => setField(l.sellerSku, e.target.value)}
                  />
                </FormField>
              )}
            </li>
          );
        })}
      </ul>
      {canRecord && (
        <>
          <p className="text-xs text-fg-tertiary">
            Enter a count only for lines Amazon has (re)checked in — current counts are shown on
            each line above.
          </p>
          {error && <p className="text-sm text-negative">{error}</p>}
          {notice && <p className="text-sm text-positive">{notice}</p>}
          <Button
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={recordReceived.isPending || !hasAnyInput}
            loading={recordReceived.isPending}
          >
            Record FBA check-in
          </Button>
        </>
      )}
    </div>
  );
}

function ShipmentFilesSection({
  shipmentId,
  files,
}: {
  shipmentId: string;
  files: FbaShipmentDetail['files'];
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const upload = useUploadShipmentFile(shipmentId);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      await upload.mutateAsync(file);
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Upload failed.');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-fg-primary">Files</p>
      {files.length === 0 && <p className="text-xs text-fg-tertiary">No files uploaded yet.</p>}
      <ul className="space-y-1">
        {files.map((f) => (
          <li key={f.id} className="text-sm">
            <a
              href={`/api/shipments/${shipmentId}/files/${f.id}`}
              className="text-accent-fg hover:underline"
            >
              {f.filename}
            </a>
            <span className="text-xs text-fg-tertiary"> · {Math.ceil(f.sizeBytes / 1024)} KB</span>
          </li>
        ))}
      </ul>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        onChange={handleFileChange}
        disabled={upload.isPending}
        className="block w-full text-sm text-fg-secondary file:mr-3 file:rounded-sm file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-fg-primary hover:file:bg-surface-3"
      />
      {error && <p className="text-sm text-negative">{error}</p>}
    </div>
  );
}

/** Primary path: VA creates in Seller Central, then one click here. */
function ImportFromAmazonCard() {
  const importShipments = useImportShipments();
  const r = importShipments.data;
  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-fg-primary">Sync from Amazon</p>
          <p className="text-xs text-fg-tertiary">
            Pulls shipments created in Seller Central — lines, allocation, and labels.
          </p>
        </div>
        <Button
          size="sm"
          disabled={importShipments.isPending}
          onClick={() => importShipments.mutate()}
        >
          {importShipments.isPending ? 'Syncing…' : 'Sync from Amazon'}
        </Button>
      </div>
      {r && (
        <p className="mt-2 text-sm text-positive">
          {r.imported} shipment{r.imported === 1 ? '' : 's'} imported
          {r.labelsFetched > 0 &&
            `, ${r.labelsFetched} label file${r.labelsFetched === 1 ? '' : 's'} fetched`}
          {r.unallocatedUnits > 0 && (
            <span className="text-warning">
              {' '}
              — {r.unallocatedUnits} units could not be allocated from stock
            </span>
          )}
          {r.imported === 0 && ' — everything already recorded'}
        </p>
      )}
      {importShipments.isError && (
        <p className="mt-2 text-sm text-negative">
          {importShipments.error instanceof Error
            ? importShipments.error.message
            : 'Import failed.'}
        </p>
      )}
    </Card>
  );
}
